/**
 * ActivityBottomPanel.js
 * Fixed lower panel for displaying activity details
 * Shows activity information in the lower section of the scheduling screen
 *
 * Features:
 * - Activity name editing
 * - Date fields (plan start/finish, current, actual, expected)
 * - Duration and progress editing
 * - Resource assignment
 * - Calendar assignment
 * - Predecessors and Successors
 * - Comments field
 * - Change history tab
 * - Fixed height (no half-screen overlay)
 * - Integrated into page layout
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { format, parseISO, addDays, differenceInDays } from "date-fns";
import {
  X,
  Calendar as CalendarIcon,
  Users,
  History,
  Edit3,
  Save,
  Loader2,
  Undo2,
  MessageSquare,
  ArrowRight,
  ChevronDown,
  Link2,
  Lock,
  AlertTriangle,
  Activity,
  Wrench,
  Package,
  Trash2,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar as CalendarComponent } from "../ui/calendar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { cn } from "../../lib/utils";
import { toast } from "sonner";
import api from "../../lib/api";
import { parseActivityUpdateResponse } from "../../lib/merge_activities";
import ActualDatePickerBody from "./ActualDatePickerBody";
import CommentsPanel from "./CommentsPanel";
import { formatDateFull, selectActivityStart, selectActivityFinish } from "../../lib/date_format";

// Maximum undo stack size
const MAX_UNDO_STACK = 20;

// Panel heights
const PANEL_HEIGHT_ACTIVE = 260; // When activity selected - compact tabbed layout
const PANEL_HEIGHT_EMPTY = 40; // When no activity selected (minimal)

// Date format helper — delegated to the timezone-safe shared utility so
// the bottom panel agrees with the activity table on every calendar day.
const formatDate = formatDateFull;

// =============================================================
// DateField — module-level component (NOT defined inside the panel
// body). Defining it inline inside ActivityBottomPanel created a fresh
// function reference every render → React's reconciler treated each
// render as a different component type → it unmounted and remounted
// the entire DateField subtree (including the popover + day grid),
// producing the visible "calendar bouncing" the user reported when
// picking an Actual Start date. Hoisting + React.memo gives it stable
// identity so React preserves its mount across parent re-renders.
//
// Props:
//   label, field, value, isActual (visual hint), disabled, disabledReason
//   activity-state-derived: isActualized, hasActualFinish, canEdit
//   editing-state: isEditing, setEditingField, handleSaveDate
//   comment-state: changeComment, setChangeComment
// =============================================================
const DateField = React.memo(function DateField({
  label,
  field,
  value,
  isActual = false,
  disabled = false,
  disabledReason = "",
  isActualized,
  hasActualFinish,
  canEdit,
  isEditing,
  setEditingField,
  handleSaveDate,
  changeComment,
  setChangeComment,
  // For actual_start / actual_finish fields: route the popover content
  // through the shared ActualDatePickerBody so the panel chrome matches
  // the row's Actual cell picker (customer May 12 visual symmetry).
  activity,
  projectDataDate,
  // Saving state — wired through to the inner picker so the Save button
  // shows a spinner during the round-trip (parity with CurrentDateField).
  isSaving = false,
}) {
  const isActualField = field === "actual_start" || field === "actual_finish";
  const dateValue = value?.split("T")[0];

  // Memoize parsed Date so Calendar `selected` has stable identity
  const selectedDate = useMemo(
    () => (dateValue ? parseISO(dateValue) : undefined),
    [dateValue],
  );

  // Lock rules:
  //  - expected_finish locked iff hasActualFinish (constraint #5/#9)
  //  - planned_finish locked iff isActualized (= actual_finish set)
  //  - actual_finish locked when actual_start is missing (props.disabled
  //    fed by the parent — constraint #9 visualization)
  const isLocked =
    disabled ||
    (field === "expected_finish" && hasActualFinish) ||
    (field === "planned_finish" && isActualized);
  const lockReason =
    disabledReason ||
    (isActualized ? "Locked: Activity has been actualized" : "");

  // For Actual Start / Actual Finish fields: when the underlying actual
  // date is set, render the same blue "actualized" treatment used by
  // CurrentDateField. Mirrors the Gantt's in-progress/completed bar
  // coloring — gives the panel a visual signal that this is the actual,
  // not just the planned, value. Only applies to the actual_* fields;
  // expected_finish stays neutral.
  const showActualizedHighlight =
    !!dateValue && (field === "actual_start" || field === "actual_finish");

  if (isLocked) {
    return (
      <div>
        {label && <Label className="text-xs text-slate-500">{label}</Label>}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 h-9 px-3 border border-slate-200 rounded-md bg-slate-50 text-slate-400">
                <Lock className="h-3.5 w-3.5 text-slate-300" />
                <span className="text-sm">
                  {dateValue ? formatDate(value) : "—"}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              <p>{lockReason}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  return (
    <div>
      {label && <Label className="text-xs text-slate-500">{label}</Label>}
      {canEdit ? (
        <Popover
          open={isEditing}
          onOpenChange={(open) => setEditingField(open ? field : null)}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal h-9",
                !dateValue && "text-slate-400",
                showActualizedHighlight && "border-blue-200 bg-blue-50/50",
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              <span className={showActualizedHighlight ? "font-medium" : ""}>
                {dateValue ? formatDate(value) : "Select date"}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className={cn(
              "p-0",
              isActualField
                ? "w-[280px] max-h-[calc(100vh-100px)] overflow-y-auto"
                : "w-auto",
            )}
            align="start"
            side="bottom"
            sideOffset={4}
            collisionPadding={16}
          >
            {isActualField ? (
              // Shared chrome for actual_start / actual_finish — same
              // calendar + footer + comment + Save/Cancel as the row
              // picker. Single source of truth.
              <ActualDatePickerBody
                initialValue={value}
                field={field}
                activity={activity}
                projectDataDate={projectDataDate}
                initialComment={changeComment}
                saving={isSaving}
                onSave={(formatted, comment) => {
                  setChangeComment(comment);
                  handleSaveDate(field, formatted);
                }}
                onCancel={() => setEditingField(null)}
              />
            ) : (
              // Generic body for expected_finish / planned_* — no
              // future-dates rule, no constraint footer, save fires on
              // date click (legacy behavior preserved).
              <>
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    if (date) {
                      const formatted = format(date, "yyyy-MM-dd");
                      handleSaveDate(field, formatted);
                    }
                  }}
                  initialFocus
                />
                <div className="p-3 border-t">
                  <Input
                    placeholder="Add comment (optional)"
                    value={changeComment}
                    onChange={(e) => setChangeComment(e.target.value)}
                    className="h-8 text-xs"
                  />
                </div>
              </>
            )}
          </PopoverContent>
        </Popover>
      ) : (
        <div
          className={cn(
            "text-sm font-medium py-2",
            showActualizedHighlight && "text-blue-700",
          )}
        >
          {dateValue ? formatDate(value) : "—"}
        </div>
      )}
    </div>
  );
});

// =============================================================
// CurrentDateField — same module-level extraction as DateField.
// Wraps the Current Start / Current Finish picker that supports
// "Mark as Actual" + comment + Save/Cancel buttons. Closes over
// activity data via props, not the parent's closure.
// =============================================================
const CurrentDateField = React.memo(function CurrentDateField({
  field,
  activity,
  isEditing,
  setEditingField,
  editDateValue,
  setEditDateValue,
  markAsActualChecked,
  setMarkAsActualChecked,
  handleSaveCurrentDate,
  changeComment,
  setChangeComment,
  canEdit,
  isSaving,
}) {
  const isStart = field === "current_start";
  const isFieldActualized = isStart
    ? Boolean(activity.actual_start)
    : Boolean(activity.actual_finish);
  // Display uses the canonical selectActivityStart/Finish helper.
  // Priority: actual → early (CPM forecast) → planned. This matches the
  // top activity table and the Gantt bar position — one law, one helper.
  const displayValue = isStart
    ? selectActivityStart(activity)
    : selectActivityFinish(activity);
  const dateValue = displayValue?.split("T")[0];

  const selectedDate = useMemo(() => {
    const raw = editDateValue || dateValue;
    return raw ? parseISO(raw) : undefined;
  }, [editDateValue, dateValue]);

  return (
    <div>
      {canEdit ? (
        <Popover
          open={isEditing}
          onOpenChange={(open) => {
            if (!open) {
              setEditingField(null);
              setEditDateValue("");
              setMarkAsActualChecked(false);
            }
          }}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal h-9",
                !dateValue && "text-slate-400",
                isFieldActualized && "border-blue-200 bg-blue-50/50",
              )}
              onClick={() => {
                setEditingField(field);
                setEditDateValue(dateValue || "");
                // Always default to unchecked. The user must EXPLICITLY opt
                // in to also writing the Actual field — editing Start should
                // not silently update Actual Start on an already-actualized
                // activity. The visual "actualized" cue stays via the blue
                // border + " (a)" suffix on the picker button itself.
                setMarkAsActualChecked(false);
              }}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              <span className={isFieldActualized ? "font-medium" : ""}>
                {dateValue ? formatDate(displayValue) : "Select date"}
                {isFieldActualized && " (a)"}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start" side="top" sideOffset={8}>
            <CalendarComponent
              mode="single"
              selected={selectedDate}
              onSelect={(date) => {
                if (date) {
                  setEditDateValue(format(date, "yyyy-MM-dd"));
                }
              }}
              initialFocus
            />
            {/* Mark as Actual checkbox */}
            <div className="flex items-center gap-2 px-3 py-2 border-t bg-slate-50">
              <input
                type="checkbox"
                id={`mark-actual-${field}`}
                checked={markAsActualChecked}
                onChange={(e) => setMarkAsActualChecked(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <label
                htmlFor={`mark-actual-${field}`}
                className="text-[11px] font-medium text-slate-700 cursor-pointer select-none"
              >
                Mark as Actual
              </label>
            </div>
            <div className="p-3 border-t">
              <Input
                placeholder="Add comment (optional)"
                value={changeComment}
                onChange={(e) => setChangeComment(e.target.value)}
                className="h-8 text-xs"
              />
            </div>
            <div className="flex items-center justify-end gap-2 p-2 border-t">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => {
                  setEditingField(null);
                  setEditDateValue("");
                  setMarkAsActualChecked(false);
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-7 text-xs"
                disabled={isSaving || !editDateValue}
                onClick={() =>
                  handleSaveCurrentDate(field, editDateValue, markAsActualChecked)
                }
              >
                {isSaving ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      ) : (
        <div className="text-sm font-medium text-slate-800 py-2">
          {dateValue ? formatDate(displayValue) : "—"}
          {isFieldActualized && " (a)"}
        </div>
      )}
    </div>
  );
});

const ActivityBottomPanel = ({
  activity: activityProp,
  isOpen,
  onClose,
  onActivityUpdate,
  // Layer 2 — fast path: merge the PUT response (activity + cascade) into
  // parent state without triggering a full schedule refetch. When provided,
  // PUT response-using sites prefer this over onActivityUpdate.
  onActivitiesMerged = null,
  // Called after a successful delete so the parent can remove the row.
  onActivityDeleted = null,
  resources = [],
  calendars = [],
  scheduleId,
  // canEdit  = EDIT_ACTIVITY (plan): planned/current dates, duration, expected
  //            finish, structure.
  // canEditActuals = EDIT_ACTUALS (collaborate+): actual start/finish, progress.
  // The two are distinct RBAC capabilities (collaborate may record progress but
  // not re-plan). Defaulting actuals to canEdit would wrongly deny collaborate
  // their entitled actuals editing (it works in the grid but was denied here).
  canEdit = false,
  canEditActuals = false,
  undoStack = [],
  setUndoStack,
  allActivities = [],
  // Project's data_date — feeds the actual_* date picker's max
  // constraint via ActualDatePickerBody (matches the row picker).
  projectDataDate = null,
  // Increment to deep-open the Comments tab (row badge click).
  openCommentsSignal = 0,
  // (entityId, count) after a comment post/delete, so row badges stay live.
  onCommentCountChange = null,
}) => {
  // Panel state - now fixed height, no resizing
  const [activeTab, setActiveTab] = useState("details");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const panelRef = useRef(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Optimistic overrides: stores saved field values for immediate UI refresh
  const [activityOverrides, setActivityOverrides] = useState({});

  // Clear overrides whenever the parent passes a fresh activityProp.
  //
  // Bug A (May 11 customer meeting): the previous dep was `activityProp?.id`,
  // which fired only on activity SWITCH. After in-place save → parent
  // refetch → server returned new state → the panel still merged its own
  // pre-save override over the fresh activityProp → row & panel diverged.
  //
  // Parent (SchedulingScreen.handleActivityChange + getAllActivities
  // useMemo) hands back a NEW object reference only when data changes —
  // so this dep clears the override exactly when stale, never gratuitously.
  useEffect(() => {
    setActivityOverrides({});
  }, [activityProp]);

  // Merge prop data with local overrides for display
  const activity = useMemo(() => {
    if (!activityProp) return null;
    if (Object.keys(activityOverrides).length === 0) return activityProp;
    return { ...activityProp, ...activityOverrides };
  }, [activityProp, activityOverrides]);

  const panelHeight = isOpen ? PANEL_HEIGHT_ACTIVE : PANEL_HEIGHT_EMPTY;

  // Edit states
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [changeComment, setChangeComment] = useState("");

  // Resources
  const [selectedResourceIds, setSelectedResourceIds] = useState([]);
  const [isAssigningResources, setIsAssigningResources] = useState(false);

  // History
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Undo
  const [isUndoing, setIsUndoing] = useState(false);
  const [showUndoConfirmDialog, setShowUndoConfirmDialog] = useState(false);

  // Current date editing (for Current Start / Current Finish calendar pickers)
  const [editDateValue, setEditDateValue] = useState("");
  const [markAsActualChecked, setMarkAsActualChecked] = useState(false);

  // Relationships
  const [predecessors, setPredecessors] = useState([]);
  const [successors, setSuccessors] = useState([]);
  const [loadingRelationships, setLoadingRelationships] = useState(false);

  // Lag editing
  const [editingRelLag, setEditingRelLag] = useState(null); // { relId, value }
  const [savingRelLag, setSavingRelLag] = useState(false);

  // Activity is "actualized" only when actual_finish is set — matches the
  // single definition in UnifiedScheduleView.js:2303 and the customer's
  // 9-constraint rules from the May 11 meeting (context.md). actual_start
  // alone means "in progress": expected_finish + planned_finish stay
  // editable so the user can forecast the new finish.
  const isActualized = Boolean(activity?.actual_finish);
  const hasActualStart = Boolean(activity?.actual_start);
  const hasActualFinish = Boolean(activity?.actual_finish);

  // Display duration from server's original_duration (server is source of truth)
  const displayDuration = useMemo(() => {
    if (!activity) return 0;
    return activity.original_duration || activity.duration || 0;
  }, [activity]);

  // Initialize state when activity changes
  useEffect(() => {
    if (activity) {
      setEditName(activity.name || "");
      setSelectedResourceIds(activity.resource_ids || []);
      setActiveTab("details");
      setEditingField(null);
      setChangeComment("");
      setEditDateValue("");
      setMarkAsActualChecked(false);
      fetchRelationships();
    }
  }, [activityProp?.id]);

  // Deep-open the Comments tab on row-badge click. Declared AFTER the
  // activity-change reset above so it wins when both fire in one commit.
  useEffect(() => {
    if (openCommentsSignal > 0) setActiveTab("comments");
  }, [openCommentsSignal]);

  // Fetch relationships
  const fetchRelationships = useCallback(async () => {
    if (!activity?.id || !scheduleId) return;

    setLoadingRelationships(true);
    try {
      const response = await api.get(`/cpm/relationships/schedule/${scheduleId}`);
      const allRels = response.data || [];

      // Filter predecessors (where this activity is successor)
      const preds = allRels.filter((r) => r.successor_id === activity.id);
      setPredecessors(preds);

      // Filter successors (where this activity is predecessor)
      const succs = allRels.filter((r) => r.predecessor_id === activity.id);
      setSuccessors(succs);
    } catch (error) {
      console.error("Failed to fetch relationships:", error);
    } finally {
      setLoadingRelationships(false);
    }
  }, [activityProp?.id, scheduleId]);

  // Update lag on an existing relationship
  const handleUpdateRelLag = async (relationshipId, newLag) => {
    setSavingRelLag(true);
    try {
      // PUT /cpm/relationships/{id} returns RelationshipMutationResponse —
      // cascade-merge the moved successors (plan zesty-coalescing-clarke.md).
      const { data } = await api.put(`/cpm/relationships/${relationshipId}`, { lag_days: newLag });
      toast.success(`Lag updated to ${newLag}d`);
      fetchRelationships();

      if (data?.cascaded?.length && onActivitiesMerged) {
        onActivitiesMerged(data.cascaded);
      } else {
        onActivityUpdate?.();
      }
    } catch (error) {
      console.error("Failed to update lag:", error);
      toast.error("Failed to update lag");
    } finally {
      setSavingRelLag(false);
      setEditingRelLag(null);
    }
  };

  // Fetch history when activity changes (always load for inline display)
  useEffect(() => {
    if (activity?.id) {
      fetchHistory();
    }
  }, [activityProp?.id]);

  const fetchHistory = async () => {
    if (!activity?.id) return;

    setLoadingHistory(true);
    try {
      const response = await api.get(
        `/history/activity/${activity.id}?page=1&page_size=50`,
      );
      setHistory(response.data.entries || []);
    } catch (error) {
      console.error("Failed to fetch history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Get resource type icon - returns proper icon based on resource type
  const getResourceTypeIcon = (type) => {
    const typeLower = type?.toLowerCase();
    switch (typeLower) {
      case "person":
      case "labor":
        return Users;
      case "equipment":
        return Wrench;
      case "material":
        return Package;
      default:
        return Users; // Default fallback icon
    }
  };

  // Push to undo stack helper
  const pushToUndoStack = ({ field, displayName, oldValue, newValue }) => {
    if (!setUndoStack || !activity) return;

    setUndoStack((prev) => {
      const newEntry = {
        id: Date.now(),
        activityId: activity.id,
        activityName: activity.name,
        field,
        displayName,
        oldValue,
        newValue,
        timestamp: new Date().toISOString(),
      };
      const newStack = [newEntry, ...prev];
      return newStack.slice(0, MAX_UNDO_STACK);
    });
  };

  // Save handlers
  const handleSaveName = async () => {
    if (!editName.trim() || editName === activity.name) {
      setIsEditingName(false);
      return;
    }

    setIsSaving(true);
    try {
      // Layer 2 fast path — name change isn't CPM-affecting so cascaded
      // will be empty, but reading the response lets the parent skip the
      // full schedule refetch entirely.
      const response = await api.put(`/activities/${activity.id}`, { name: editName.trim() });
      const { activity: serverActivity, cascaded } = parseActivityUpdateResponse(response.data);

      pushToUndoStack({
        field: "name",
        displayName: "Activity Name",
        oldValue: activity.name,
        newValue: editName.trim(),
      });

      setActivityOverrides(prev => ({ ...prev, name: editName.trim() }));
      toast.success("Activity name updated");
      if (onActivitiesMerged) onActivitiesMerged([serverActivity, ...cascaded]);
      else onActivityUpdate?.();
      setIsEditingName(false);
    } catch (error) {
      console.error("Failed to update name:", error);
      toast.error("Failed to update activity name");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDate = async (field, value) => {
    // Validation: Cannot edit expected_finish or current finish if actual_finish exists
    if (
      isActualized &&
      (field === "expected_finish" || field === "planned_finish")
    ) {
      toast.error("Cannot modify this field after activity is actualized");
      setEditingField(null);
      return;
    }

    setIsSaving(true);
    try {
      const updateData = { [field]: value };
      if (changeComment.trim()) {
        updateData.comment = changeComment.trim();
      }

      // If setting actual_finish, clear expected_finish automatically
      if (field === "actual_finish" && value) {
        updateData.expected_finish = null;
      }

      // Route actual_start / actual_finish through PUT /progress so the
      // canonical actuals pipeline fires: status auto-derivation,
      // constraint #5 (actual_finish → duration), constraint #5C
      // (actual_start → duration), constraint #9 validation
      // (actual_finish requires actual_start), and synchronous CPM
      // (Bug J fix). PUT /activities accepts these fields too but
      // doesn't run that pipeline — bottom panel and row would diverge.
      // The /progress endpoint takes only progress-relevant fields,
      // not 'comment' — strip before send.
      // Layer 2 fast path — both branches return ActivityUpdateResponse.
      // Capture the response so we can merge the cascade in place.
      const isActualField = field === "actual_start" || field === "actual_finish";
      const response = isActualField
        ? await api.put(`/activities/${activity.id}/progress`, { [field]: value })
        : await api.put(`/activities/${activity.id}`, updateData);
      const { activity: serverActivity, cascaded } = parseActivityUpdateResponse(response.data);

      const displayNames = {
        planned_start: "Plan Start",
        planned_finish: "Plan Finish",
        actual_start: "Actual Start",
        actual_finish: "Actual Finish",
        expected_finish: "Expected Finish",
      };

      // For actualization, store comprehensive undo data
      if (field === "actual_finish" || field === "actual_start") {
        pushToUndoStack({
          field,
          displayName: displayNames[field] || field,
          oldValue: activity[field]?.split("T")[0] || null,
          newValue: value,
          isActualization: true,
          previousState: {
            actual_start: activity.actual_start?.split("T")[0] || null,
            actual_finish: activity.actual_finish?.split("T")[0] || null,
            expected_finish: activity.expected_finish?.split("T")[0] || null,
            planned_start: activity.planned_start?.split("T")[0] || null,
            planned_finish: activity.planned_finish?.split("T")[0] || null,
            status: activity.status,
          },
        });
      } else {
        pushToUndoStack({
          field,
          displayName: displayNames[field] || field,
          oldValue: activity[field]?.split("T")[0],
          newValue: value,
        });
      }

      setActivityOverrides(prev => ({ ...prev, ...updateData }));
      toast.success("Date updated");
      if (onActivitiesMerged) onActivitiesMerged([serverActivity, ...cascaded]);
      else onActivityUpdate?.();
      setEditingField(null);
      setChangeComment("");
    } catch (error) {
      console.error("Failed to update date:", error);
      toast.error("Failed to update date");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDuration = async (value) => {
    const duration = parseInt(value, 10);
    if (isNaN(duration) || duration < 1) {
      toast.error("Duration must be at least 1 day");
      return;
    }

    setIsSaving(true);
    try {
      let updateData = {
        original_duration: duration,
      };

      if (changeComment.trim()) {
        updateData.comment = changeComment.trim();
      }

      // Server recalculates finish from start + duration using the activity's calendar.
      // Layer 2 — response is ActivityUpdateResponse: { activity, cascaded, cpm_ran }.
      const response = await api.put(`/activities/${activity.id}`, updateData);
      const { activity: serverActivity, cascaded } = parseActivityUpdateResponse(response.data);

      pushToUndoStack({
        field: "original_duration",
        displayName: "Duration",
        oldValue: activity.original_duration || activity.duration,
        newValue: duration,
      });

      // Use server-returned values for the optimistic override
      setActivityOverrides(prev => ({
        ...prev,
        original_duration: serverActivity.original_duration,
        remaining_duration: serverActivity.remaining_duration,
        planned_start: serverActivity.planned_start,
        planned_finish: serverActivity.planned_finish,
        early_start: serverActivity.early_start,
        early_finish: serverActivity.early_finish,
      }));
      toast.success("Duration updated. Finish date recalculated.");
      // Prefer the merge callback (no refetch) over the legacy refresh signal.
      if (onActivitiesMerged) onActivitiesMerged([serverActivity, ...cascaded]);
      else onActivityUpdate?.();
      setEditingField(null);
      setChangeComment("");
    } catch (error) {
      console.error("Failed to update duration:", error);
      toast.error("Failed to update duration");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveProgress = async (value) => {
    if (!canEdit) return; // % Complete is owner-only (EDIT_ACTIVITY); backend /progress also rejects it below PLAN
    const progress = parseFloat(value);
    if (isNaN(progress) || progress < 0 || progress > 100) {
      toast.error("Progress must be between 0 and 100");
      return;
    }

    setIsSaving(true);
    try {
      // Layer 2 fast path — progress edits run CPM (status auto-derivation),
      // so cascaded may include successors whose dates shifted.
      const response = await api.put(`/activities/${activity.id}/progress`, {
        percent_complete: progress,
      });
      const { activity: serverActivity, cascaded } = parseActivityUpdateResponse(response.data);

      pushToUndoStack({
        field: "percent_complete",
        displayName: "Progress",
        oldValue: activity.percent_complete || 0,
        newValue: progress,
      });

      setActivityOverrides(prev => ({ ...prev, percent_complete: progress }));
      toast.success("Progress updated");
      if (onActivitiesMerged) onActivitiesMerged([serverActivity, ...cascaded]);
      else onActivityUpdate?.();
      setEditingField(null);
      setChangeComment("");
    } catch (error) {
      console.error("Failed to update progress:", error);
      toast.error("Failed to update progress");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCalendarChange = async (calendarId) => {
    setIsSaving(true);
    try {
      // Layer 2 fast path — calendar swap shifts working-day math for this
      // activity AND every CPM-driven successor.
      const response = await api.put(`/activities/${activity.id}`, {
        calendar_id: calendarId === "default" ? null : calendarId,
      });
      const { activity: serverActivity, cascaded } = parseActivityUpdateResponse(response.data);

      pushToUndoStack({
        field: "calendar_id",
        displayName: "Calendar",
        oldValue: activity.calendar_id,
        newValue: calendarId === "default" ? null : calendarId,
      });

      setActivityOverrides(prev => ({ ...prev, calendar_id: calendarId === "default" ? null : calendarId }));
      toast.success("Calendar updated");
      if (onActivitiesMerged) onActivitiesMerged([serverActivity, ...cascaded]);
      else onActivityUpdate?.();
    } catch (error) {
      console.error("Failed to update calendar:", error);
      toast.error("Failed to update calendar");
    } finally {
      setIsSaving(false);
    }
  };

  // Save current start or current finish with optional actualization
  const handleSaveCurrentDate = async (field, value, markAsActual) => {
    if (!value) {
      toast.error("Please select a date");
      setEditingField(null);
      return;
    }

    setIsSaving(true);
    try {
      let updateData = {};
      const displayName =
        field === "current_start" ? "Start" : "Finish";

      if (field === "current_start") {
        // Send only the start date change — server recalculates duration
        if (markAsActual) {
          updateData.actual_start = value;
          updateData.planned_start = value;
        } else {
          updateData.planned_start = value;
        }
      } else if (field === "current_finish") {
        // Send only the finish date change — server recalculates duration
        if (markAsActual) {
          updateData.actual_finish = value;
          updateData.planned_finish = value;
        } else {
          updateData.planned_finish = value;
        }

        // If setting actual_finish, clear expected_finish
        if (markAsActual && value) {
          updateData.expected_finish = null;
        }
      }

      if (changeComment.trim()) {
        updateData.comment = changeComment.trim();
      }

      // Server recalculates duration and syncs dates using the calendar.
      // Layer 2 — response is ActivityUpdateResponse: { activity, cascaded, cpm_ran }.
      const response = await api.put(`/activities/${activity.id}`, updateData);
      const { activity: serverActivity, cascaded } = parseActivityUpdateResponse(response.data);

      // Push to undo stack
      const undoField =
        field === "current_start"
          ? markAsActual
            ? "actual_start"
            : "planned_start"
          : markAsActual
            ? "actual_finish"
            : "planned_finish";

      pushToUndoStack({
        field: undoField,
        displayName,
        oldValue: activity[undoField]?.split("T")[0] || null,
        newValue: value,
        isActualization: markAsActual,
        previousState: markAsActual
          ? {
              actual_start: activity.actual_start?.split("T")[0] || null,
              actual_finish: activity.actual_finish?.split("T")[0] || null,
              expected_finish:
                activity.expected_finish?.split("T")[0] || null,
              planned_start: activity.planned_start?.split("T")[0] || null,
              planned_finish:
                activity.planned_finish?.split("T")[0] || null,
              status: activity.status,
            }
          : undefined,
      });

      // Use server-returned values for optimistic override
      setActivityOverrides(prev => ({
        ...prev,
        ...updateData,
        original_duration: serverActivity.original_duration,
        remaining_duration: serverActivity.remaining_duration,
        planned_start: serverActivity.planned_start,
        planned_finish: serverActivity.planned_finish,
        early_start: serverActivity.early_start,
        early_finish: serverActivity.early_finish,
      }));
      toast.success(`${displayName} updated`);
      // Layer 2 — fast path. `cascaded` came from the same PUT response above.
      if (onActivitiesMerged) onActivitiesMerged([serverActivity, ...cascaded]);
      else onActivityUpdate?.();
      setEditingField(null);
      setEditDateValue("");
      setMarkAsActualChecked(false);
      setChangeComment("");
    } catch (error) {
      console.error("Failed to update date:", error);
      toast.error("Failed to update date");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveResources = async () => {
    setIsAssigningResources(true);
    try {
      const updateData = { resource_ids: selectedResourceIds };
      if (changeComment.trim()) {
        updateData.comment = changeComment.trim();
      }

      // Layer 2 fast path — resource changes don't trigger CPM, but reading
      // the response still skips the full refetch.
      const response = await api.put(`/activities/${activity.id}`, updateData);
      const { activity: serverActivity, cascaded } = parseActivityUpdateResponse(response.data);

      pushToUndoStack({
        field: "resource_ids",
        displayName: "Resources",
        oldValue: activity.resource_ids || [],
        newValue: selectedResourceIds,
      });

      setActivityOverrides(prev => ({ ...prev, resource_ids: selectedResourceIds }));
      toast.success("Resources updated");
      if (onActivitiesMerged) onActivitiesMerged([serverActivity, ...cascaded]);
      else onActivityUpdate?.();
      setChangeComment("");
    } catch (error) {
      console.error("Failed to update resources:", error);
      toast.error("Failed to update resources");
    } finally {
      setIsAssigningResources(false);
    }
  };

  // Check if last undo is an actualization change
  const isLastChangeActualization = useCallback(() => {
    if (!undoStack || undoStack.length === 0) return false;
    const lastChange = undoStack[0];
    return (
      lastChange.isActualization ||
      lastChange.field === "actual_finish" ||
      lastChange.field === "actual_start"
    );
  }, [undoStack]);

  // Request undo - show confirmation if activity is actualized (has actual start or actual finish)
  const requestUndo = () => {
    if (!undoStack || undoStack.length === 0) return;
    const lastChange = undoStack[0];
    if (lastChange.activityId !== activity?.id) return;

    // If activity is actualized (has actual_start OR actual_finish), show confirmation
    if (isActualized) {
      setShowUndoConfirmDialog(true);
    } else {
      handleUndo();
    }
  };

  // Undo handler
  const handleUndo = async () => {
    if (!undoStack || undoStack.length === 0) return;

    const lastChange = undoStack[0];
    if (lastChange.activityId !== activity?.id) return;

    setIsUndoing(true);
    setShowUndoConfirmDialog(false);

    try {
      let updateData = {};

      // For actualization undo, restore previous state comprehensively
      if (lastChange.isActualization && lastChange.previousState) {
        updateData = {
          [lastChange.field]: lastChange.oldValue,
          // Restore expected_finish if it was cleared
          expected_finish: lastChange.previousState.expected_finish || null,
        };
      } else {
        updateData = { [lastChange.field]: lastChange.oldValue };
      }

      if (lastChange.field === "percent_complete") {
        await api.put(`/activities/${activity.id}/progress`, updateData);
      } else {
        await api.put(`/activities/${activity.id}`, updateData);
      }

      // Log undo to history
      try {
        await api.post(`/history/activity/${activity.id}`, {
          action_type: "undo",
          field_changes: [
            {
              field: lastChange.field,
              display_name: `Undo: ${lastChange.displayName}`,
              old_value: lastChange.newValue,
              new_value: lastChange.oldValue,
            },
          ],
          comment: `Reverted ${lastChange.displayName} from ${lastChange.newValue || "(empty)"} to ${lastChange.oldValue || "(empty)"}`,
        });
      } catch (historyError) {
        console.warn("Failed to log undo to history:", historyError);
      }

      setActivityOverrides(prev => ({ ...prev, ...updateData }));
      setUndoStack((prev) => prev.slice(1));
      toast.success(`Reverted ${lastChange.displayName}`);
      onActivityUpdate?.();
    } catch (error) {
      console.error("Failed to undo:", error);
      toast.error("Failed to undo change");
    } finally {
      setIsUndoing(false);
    }
  };

  // formatDate is defined at module scope (line ~81) so the extracted
  // DateField / CurrentDateField components below can use it without
  // prop-drilling. The inner duplicate that used to live here has been
  // removed — both call sites now resolve to the module-level function.

  // Get activity name by ID
  const getActivityName = (activityId) => {
    const act = allActivities.find((a) => a.id === activityId);
    return act ? `${act.activity_id} - ${act.name}` : activityId;
  };

  // Resolve predecessor display from relationship (prefers backend-provided fields)
  const getPredecessorDisplay = (rel) => {
    if (rel.predecessor_activity_id && rel.predecessor_name) {
      return { id: rel.predecessor_activity_id, name: rel.predecessor_name };
    }
    // Fallback: look up from allActivities
    const act = allActivities.find((a) => a.id === rel.predecessor_id);
    if (act) return { id: act.activity_id, name: act.name };
    // Last resort: show the raw id (old behavior)
    return { id: "—", name: rel.predecessor_id };
  };

  const getSuccessorDisplay = (rel) => {
    if (rel.successor_activity_id && rel.successor_name) {
      return { id: rel.successor_activity_id, name: rel.successor_name };
    }
    const act = allActivities.find((a) => a.id === rel.successor_id);
    if (act) return { id: act.activity_id, name: act.name };
    return { id: "—", name: rel.successor_id };
  };

  const hasUndoableChanges =
    undoStack &&
    undoStack.length > 0 &&
    undoStack[0]?.activityId === activity?.id;

  // Status config for display
  const statusConfig = {
    not_started: {
      label: "Not Started",
      className: "bg-slate-100 text-slate-600 border-slate-200",
    },
    in_progress: {
      label: "In Progress",
      className: "bg-blue-100 text-blue-600 border-blue-200",
    },
    completed: {
      label: "Completed",
      className: "bg-green-100 text-green-600 border-green-200",
    },
    on_hold: {
      label: "On Hold",
      className: "bg-amber-100 text-amber-600 border-amber-200",
    },
  };

  // If no activity selected, show minimal empty state
  if (!activity) {
    return (
      <div
        className="h-10 bg-slate-50/80 border-t border-slate-200 flex items-center justify-center gap-2 shrink-0"
        data-testid="activity-panel-empty"
      >
        <Activity className="h-3.5 w-3.5 text-slate-300" />
        <p className="text-xs text-slate-400">
          Click an activity to view details
        </p>
      </div>
    );
  }

  const currentStatus =
    statusConfig[activity.status] || statusConfig.not_started;

  const handleDeleteActivity = async () => {
    if (!activity?.id) return;
    setIsDeleting(true);
    try {
      const { data } = await api.delete(`/activities/${activity.id}`);
      toast.success(`"${activity.name}" deleted`);
      setShowDeleteConfirm(false);
      if (data?.cascaded?.length) {
        onActivitiesMerged?.(data.cascaded);
      }
      onActivityDeleted?.(activity.id);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete activity");
    } finally {
      setIsDeleting(false);
    }
  };

  // If panel is closed but activity is still selected, show minimal bar
  if (!isOpen) {
    return (
      <div
        className="h-10 bg-slate-50/80 border-t border-slate-200 flex items-center justify-center gap-2 shrink-0"
        data-testid="activity-panel-collapsed"
      >
        <Activity className="h-3.5 w-3.5 text-slate-300" />
        <p className="text-xs text-slate-400">
          Click an activity to view details
        </p>
      </div>
    );
  }

  return (
    <div
      ref={panelRef}
      className="w-full bg-white border-t-2 border-slate-200 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] flex flex-col shrink-0 relative overflow-hidden"
      style={{ height: isCollapsed ? 48 : panelHeight }}
      data-testid="activity-bottom-panel"
    >
      {/* ─── Panel Header ─── */}
      <div className="flex items-center justify-between px-5 py-1.5 border-b border-slate-200 bg-white shrink-0">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Activity ID Badge */}
          <Badge className="bg-purple-100 text-purple-700 border-purple-200 font-mono text-xs px-2 py-0.5 shrink-0">
            {activity.activity_id}
          </Badge>

          {/* Editable Activity Name */}
          {isEditingName ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="h-8 text-sm font-medium max-w-[280px]"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveName();
                  if (e.key === "Escape") {
                    setEditName(activity.name);
                    setIsEditingName(false);
                  }
                }}
              />
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
                onClick={handleSaveName}
                disabled={isSaving}
              >
                {isSaving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5 text-green-600" />
                )}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
                onClick={() => {
                  setEditName(activity.name);
                  setIsEditingName(false);
                }}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-semibold text-sm text-slate-800 truncate max-w-[300px]">
                {activity.name}
              </span>
              {canEdit && (
                <button
                  className="p-1 hover:bg-slate-100 rounded transition-colors shrink-0"
                  onClick={() => setIsEditingName(true)}
                >
                  <Edit3 className="h-3.5 w-3.5 text-slate-400" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Right side: Undo + Status + Close */}
        <div className="flex items-center gap-3">
          {/* Undo Button */}
          {canEdit && hasUndoableChanges && (
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "h-8 px-2.5 gap-1.5 text-xs",
                isLastChangeActualization()
                  ? "text-amber-600 hover:bg-amber-50"
                  : "text-orange-600 hover:bg-orange-50",
              )}
              onClick={requestUndo}
              disabled={isUndoing}
            >
              {isUndoing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Undo2 className="h-3.5 w-3.5" />
              )}
              <span>Undo</span>
            </Button>
          )}

          {/* Status Badge */}
          <Badge
            className={cn(
              "text-xs px-3 py-1 font-medium",
              currentStatus.className,
            )}
          >
            {currentStatus.label}
          </Badge>
          <ChevronDown className="h-4 w-4 text-slate-400 -ml-1.5" />

          {/* Delete activity */}
          {canEdit && (
            <button
              className="p-1.5 hover:bg-red-50 rounded-md transition-colors"
              title="Delete activity"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="h-4 w-4 text-red-400 hover:text-red-600" />
            </button>
          )}

          {/* Close */}
          <button
            className="p-1.5 hover:bg-slate-100 rounded-md transition-colors"
            onClick={onClose}
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
      </div>

      {/* ─── Tab Navigation ─── */}
      <div className="flex items-center gap-1 px-5 border-b border-slate-200 bg-white shrink-0">
        {[
          { key: "details", label: "Dates", icon: CalendarIcon },
          { key: "resources", label: "Resources", icon: Users },
          { key: "dependencies", label: "Dependencies", icon: Link2 },
          { key: "history", label: "History", icon: History },
          { key: "comments", label: "Comments", icon: MessageSquare },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                "flex items-center gap-2 px-4 py-1.5 text-sm font-medium border-b-2 transition-colors",
                isActive
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300",
              )}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ─── Tab Content ─── */}
      <div className="flex-1 overflow-auto">
        {/* ═══ DATES TAB ═══ */}
        {activeTab === "details" && (
          <div className="px-5 py-2.5">
            {/* Row 1 — the four primary date fields (mirrors row column order):
                  Current Start | Current Finish | Actual Start | Actual Finish
                Customer rule (May 11 meeting, context.md 22:32 — Amol):
                  "whatever we are seeing in the schedule structure list in the
                   columns needs to be reflected in the bottom panel."
                Actual fields use the same DateField component; their save handler
                routes through PUT /progress so #5, #5C, #9 fire identically to
                the row's actual editor. */}
            {/* Common prop bundles for the extracted date-field components.
                Wrapping in objects (NOT useMemo — these are recreated per
                render but the COMPONENT identity is now stable since the
                components are at module scope, so React.memo skips
                re-renders by shallow-comparing the props anyway). */}
            <div className="grid grid-cols-4 gap-4 mb-3">
              <div>
                <label className="text-[11px] font-medium text-slate-500 uppercase tracking-wider block mb-1.5">
                  Start
                </label>
                <CurrentDateField
                  field="current_start"
                  activity={activity}
                  isEditing={editingField === "current_start"}
                  setEditingField={setEditingField}
                  editDateValue={editDateValue}
                  setEditDateValue={setEditDateValue}
                  markAsActualChecked={markAsActualChecked}
                  setMarkAsActualChecked={setMarkAsActualChecked}
                  handleSaveCurrentDate={handleSaveCurrentDate}
                  changeComment={changeComment}
                  setChangeComment={setChangeComment}
                  canEdit={canEdit}
                  isSaving={isSaving}
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-500 uppercase tracking-wider block mb-1.5">
                  Finish
                </label>
                <CurrentDateField
                  field="current_finish"
                  activity={activity}
                  isEditing={editingField === "current_finish"}
                  setEditingField={setEditingField}
                  editDateValue={editDateValue}
                  setEditDateValue={setEditDateValue}
                  markAsActualChecked={markAsActualChecked}
                  setMarkAsActualChecked={setMarkAsActualChecked}
                  handleSaveCurrentDate={handleSaveCurrentDate}
                  changeComment={changeComment}
                  setChangeComment={setChangeComment}
                  canEdit={canEdit}
                  isSaving={isSaving}
                />
              </div>
              <div data-testid="bottom-panel-actual-start-field">
                <label className="text-[11px] font-medium text-slate-500 uppercase tracking-wider block mb-1.5">
                  Actual Start
                </label>
                <DateField
                  label=""
                  field="actual_start"
                  value={activity.actual_start}
                  isActualized={isActualized}
                  hasActualFinish={hasActualFinish}
                  canEdit={canEditActuals}
                  isEditing={editingField === "actual_start"}
                  setEditingField={setEditingField}
                  handleSaveDate={handleSaveDate}
                  changeComment={changeComment}
                  setChangeComment={setChangeComment}
                  activity={activity}
                  projectDataDate={projectDataDate}
                  isSaving={isSaving}
                />
              </div>
              <div data-testid="bottom-panel-actual-finish-field">
                <label className="text-[11px] font-medium text-slate-500 uppercase tracking-wider block mb-1.5">
                  Actual Finish
                </label>
                <DateField
                  label=""
                  field="actual_finish"
                  value={activity.actual_finish}
                  disabled={!hasActualStart}
                  disabledReason="Set Actual Start first (constraint #9)"
                  isActualized={isActualized}
                  hasActualFinish={hasActualFinish}
                  canEdit={canEditActuals}
                  isEditing={editingField === "actual_finish"}
                  setEditingField={setEditingField}
                  handleSaveDate={handleSaveDate}
                  changeComment={changeComment}
                  setChangeComment={setChangeComment}
                  activity={activity}
                  projectDataDate={projectDataDate}
                  isSaving={isSaving}
                />
              </div>
            </div>

            {/* Row 2 — Expected Finish, Duration, Progress, Calendar */}
            <div className="grid grid-cols-4 gap-4 mb-3">
              <div>
                <label className="text-[11px] font-medium text-slate-500 uppercase tracking-wider block mb-1.5">
                  Expected Finish
                </label>
                <DateField
                  label=""
                  field="expected_finish"
                  value={activity.expected_finish}
                  isActualized={isActualized}
                  hasActualFinish={hasActualFinish}
                  canEdit={canEdit}
                  isEditing={editingField === "expected_finish"}
                  setEditingField={setEditingField}
                  handleSaveDate={handleSaveDate}
                  changeComment={changeComment}
                  setChangeComment={setChangeComment}
                  isSaving={isSaving}
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-slate-500 uppercase tracking-wider block mb-1.5">
                  Duration
                </label>
                {canEdit && editingField === "duration" ? (
                  <Input
                    type="number"
                    min="1"
                    defaultValue={displayDuration}
                    className="h-9 text-sm w-24"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveDuration(e.target.value);
                      if (e.key === "Escape") setEditingField(null);
                    }}
                    onBlur={(e) => handleSaveDuration(e.target.value)}
                  />
                ) : (
                  <div
                    className={cn(
                      "text-sm font-medium text-slate-800 py-2",
                      canEdit && "cursor-pointer hover:text-blue-600",
                    )}
                    onClick={() => canEdit && setEditingField("duration")}
                  >
                    {displayDuration} days
                  </div>
                )}
              </div>
              {/* Progress + Calendar render below in the same grid */}
              {/* Progress */}
              <div>
                <label className="text-[11px] font-medium text-slate-500 uppercase tracking-wider block mb-1.5">
                  Progress
                </label>
                {/* Progress (% Complete) is a full-schedule field — owner-only
                    (canEdit = EDIT_ACTIVITY), NOT the Collaborator actuals
                    surface (Roles_and_Permissions_Spec §7). */}
                {canEdit && editingField === "progress" ? (
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    defaultValue={activity.percent_complete || 0}
                    className="h-9 text-sm w-24"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter")
                        handleSaveProgress(e.target.value);
                      if (e.key === "Escape") setEditingField(null);
                    }}
                    onBlur={(e) => handleSaveProgress(e.target.value)}
                  />
                ) : (
                  <div
                    className={cn(
                      "flex items-center gap-2 py-2",
                      canEdit && "cursor-pointer",
                    )}
                    onClick={() => canEdit && setEditingField("progress")}
                  >
                    <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full transition-all"
                        style={{
                          width: `${activity.percent_complete || 0}%`,
                        }}
                      />
                    </div>
                    <span
                      className={cn(
                        "text-sm font-medium text-slate-700",
                        canEdit && "hover:text-blue-600",
                      )}
                    >
                      {activity.percent_complete || 0}%
                    </span>
                  </div>
                )}
              </div>
              {/* Calendar */}
              <div>
                <label className="text-[11px] font-medium text-slate-500 uppercase tracking-wider block mb-1.5">
                  Calendar
                </label>
                {canEdit ? (
                  <Select
                    value={activity.calendar_id || "default"}
                    onValueChange={handleCalendarChange}
                    disabled={isSaving}
                  >
                    <SelectTrigger className="h-9 text-sm w-full">
                      <SelectValue placeholder="Project Default" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Project Default</SelectItem>
                      {calendars.filter(cal => cal.id).map((cal) => (
                        <SelectItem key={cal.id} value={cal.id}>
                          {cal.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="text-sm font-medium text-slate-800 py-2">
                    {calendars.find((c) => c.id === activity.calendar_id)
                      ?.name || "Project Default"}
                  </div>
                )}
              </div>
              {/* Row 2 has exactly 4 cols: EF, Dur, Progress, Calendar — no padding cells needed */}
            </div>

            {/* Row 3: Change Comment */}
            <div className="border-t border-slate-100 pt-2.5">
              <label className="text-[11px] font-medium text-slate-500 uppercase tracking-wider flex items-center gap-1.5 mb-1.5">
                <MessageSquare className="h-3.5 w-3.5" />
                Change Comment
              </label>
              <Input
                placeholder="Add a comment for the next change..."
                value={changeComment}
                onChange={(e) => setChangeComment(e.target.value)}
                className="h-9 text-sm max-w-xl"
              />
            </div>
          </div>
        )}

        {/* ═══ RESOURCES TAB ═══ */}
        {activeTab === "resources" && (
          <div className="px-6 py-4">
            <div className="flex items-center gap-3 mb-4">
              <h4 className="text-sm font-medium text-slate-700">
                Assigned Resources
              </h4>
              {canEdit && (
                <Select
                  onValueChange={(value) => {
                    if (!selectedResourceIds.includes(value))
                      setSelectedResourceIds((prev) => [...prev, value]);
                  }}
                >
                  <SelectTrigger className="h-8 text-xs w-40">
                    <SelectValue placeholder="+ Add Resource" />
                  </SelectTrigger>
                  <SelectContent>
                    {resources
                      .filter((r) => r.id && !selectedResourceIds.includes(r.id))
                      .map((r) => {
                        const IconComponent = getResourceTypeIcon(
                          r.resource_type,
                        );
                        return (
                          <SelectItem
                            key={r.id}
                            value={r.id}
                            className="text-sm"
                          >
                            <span className="flex items-center gap-2">
                              <IconComponent className="h-3.5 w-3.5 text-slate-400" />
                              {r.name}
                            </span>
                          </SelectItem>
                        );
                      })}
                  </SelectContent>
                </Select>
              )}
              {canEdit &&
                JSON.stringify(selectedResourceIds.sort()) !==
                  JSON.stringify((activity.resource_ids || []).sort()) && (
                  <Button
                    size="sm"
                    onClick={handleSaveResources}
                    disabled={isAssigningResources}
                    className="h-8 text-xs px-3"
                  >
                    {isAssigningResources ? (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    ) : null}
                    Save Changes
                  </Button>
                )}
            </div>

            {selectedResourceIds.length === 0 ? (
              <div className="text-sm text-slate-400 italic py-2">
                No resources assigned
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {selectedResourceIds.map((resourceId) => {
                  const resource = resources.find((r) => r.id === resourceId);
                  if (!resource) return null;
                  const IconComponent = getResourceTypeIcon(
                    resource.resource_type,
                  );
                  return (
                    <Badge
                      key={resourceId}
                      variant="secondary"
                      className="h-7 text-xs px-2.5 py-1 flex items-center gap-1.5"
                    >
                      <IconComponent className="h-3.5 w-3.5 text-slate-500" />
                      {resource.name}
                      {canEdit && (
                        <X
                          className="h-3 w-3 ml-1 cursor-pointer text-slate-400 hover:text-red-500 transition-colors"
                          onClick={() =>
                            setSelectedResourceIds((prev) =>
                              prev.filter((id) => id !== resourceId),
                            )
                          }
                        />
                      )}
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ═══ DEPENDENCIES TAB ═══ */}
        {activeTab === "dependencies" && (
          <div className="px-6 py-4">
            <div className="grid grid-cols-2 gap-8">
              {/* Predecessors */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <ArrowRight className="h-4 w-4 text-slate-400 rotate-180" />
                  Predecessors
                </h4>
                {loadingRelationships ? (
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading...
                  </div>
                ) : predecessors.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">
                    No predecessors
                  </p>
                ) : (
                  <div className="space-y-2">
                    {predecessors.map((rel) => {
                      const disp = getPredecessorDisplay(rel);
                      const relId = rel.id || rel.relationship_id;
                      return (
                        <div
                          key={relId}
                          className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-md border border-slate-100"
                        >
                          <Badge variant="outline" className="text-xs font-mono">
                            {disp.id}
                          </Badge>
                          <span className="text-sm text-slate-700 truncate">
                            {disp.name}
                          </span>
                          <Badge
                            variant="secondary"
                            className="text-[10px] ml-auto shrink-0"
                          >
                            {rel.relationship_type || "FS"}
                          </Badge>
                          {canEdit && editingRelLag?.relId === relId ? (
                            <div className="flex items-center gap-1 shrink-0">
                              <Input
                                type="number"
                                value={editingRelLag.value}
                                onChange={(e) => setEditingRelLag({ ...editingRelLag, value: parseInt(e.target.value) || 0 })}
                                className="h-6 w-16 text-xs px-1.5"
                                autoFocus
                                disabled={savingRelLag}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleUpdateRelLag(relId, editingRelLag.value);
                                  if (e.key === 'Escape') setEditingRelLag(null);
                                }}
                                onBlur={() => handleUpdateRelLag(relId, editingRelLag.value)}
                              />
                              <span className="text-[10px] text-slate-400">d</span>
                            </div>
                          ) : (
                            <span
                              className={cn(
                                "text-xs shrink-0",
                                canEdit && "cursor-pointer hover:underline",
                                rel.lag_days > 0 ? "text-amber-600 font-medium" : rel.lag_days < 0 ? "text-green-600 font-medium" : "text-slate-400"
                              )}
                              onClick={() => canEdit && setEditingRelLag({ relId, value: rel.lag_days || 0 })}
                              title={canEdit ? "Click to edit lag" : undefined}
                            >
                              Lag: {rel.lag_days || 0}d
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Successors */}
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <ArrowRight className="h-4 w-4 text-slate-400" />
                  Successors
                </h4>
                {loadingRelationships ? (
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading...
                  </div>
                ) : successors.length === 0 ? (
                  <p className="text-sm text-slate-400 italic">No successors</p>
                ) : (
                  <div className="space-y-2">
                    {successors.map((rel) => {
                      const disp = getSuccessorDisplay(rel);
                      const relId = rel.id || rel.relationship_id;
                      return (
                        <div
                          key={relId}
                          className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-md border border-slate-100"
                        >
                          <Badge variant="outline" className="text-xs font-mono">
                            {disp.id}
                          </Badge>
                          <span className="text-sm text-slate-700 truncate">
                            {disp.name}
                          </span>
                          <Badge
                            variant="secondary"
                            className="text-[10px] ml-auto shrink-0"
                          >
                            {rel.relationship_type || "FS"}
                          </Badge>
                          {canEdit && editingRelLag?.relId === relId ? (
                            <div className="flex items-center gap-1 shrink-0">
                              <Input
                                type="number"
                                value={editingRelLag.value}
                                onChange={(e) => setEditingRelLag({ ...editingRelLag, value: parseInt(e.target.value) || 0 })}
                                className="h-6 w-16 text-xs px-1.5"
                                autoFocus
                                disabled={savingRelLag}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleUpdateRelLag(relId, editingRelLag.value);
                                  if (e.key === 'Escape') setEditingRelLag(null);
                                }}
                                onBlur={() => handleUpdateRelLag(relId, editingRelLag.value)}
                              />
                              <span className="text-[10px] text-slate-400">d</span>
                            </div>
                          ) : (
                            <span
                              className={cn(
                                "text-xs shrink-0",
                                canEdit && "cursor-pointer hover:underline",
                                rel.lag_days > 0 ? "text-amber-600 font-medium" : rel.lag_days < 0 ? "text-green-600 font-medium" : "text-slate-400"
                              )}
                              onClick={() => canEdit && setEditingRelLag({ relId, value: rel.lag_days || 0 })}
                              title={canEdit ? "Click to edit lag" : undefined}
                            >
                              Lag: {rel.lag_days || 0}d
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ═══ HISTORY TAB ═══ */}
        {activeTab === "history" && (
          <div className="px-6 py-4">
            <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <History className="h-4 w-4 text-slate-400" />
              Change History
            </h4>
            {loadingHistory ? (
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading history...
              </div>
            ) : history.length === 0 ? (
              <p className="text-sm text-slate-400 italic">
                No changes recorded
              </p>
            ) : (
              <div className="space-y-2 max-h-[220px] overflow-auto">
                {history.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-start gap-3 px-3 py-2.5 bg-slate-50 rounded-md border border-slate-100"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-medium text-slate-700">
                          {entry.user_name || "System"}
                        </span>
                        <span className="text-[10px] text-slate-400">
                          {entry.timestamp
                            ? format(
                                parseISO(entry.timestamp),
                                "MMM d, yyyy h:mm a",
                              )
                            : ""}
                        </span>
                      </div>
                      {entry.field_changes &&
                        entry.field_changes.map((change, i) => (
                          <div key={i} className="text-xs text-slate-600">
                            <span className="font-medium">
                              {change.display_name || change.field}
                            </span>
                            {change.old_value && (
                              <span className="text-slate-400"> from </span>
                            )}
                            {change.old_value && (
                              <span className="line-through text-slate-400">
                                {change.old_value}
                              </span>
                            )}
                            <span className="text-slate-400"> → </span>
                            <span className="text-blue-600 font-medium">
                              {change.new_value || "(empty)"}
                            </span>
                          </div>
                        ))}
                      {entry.comment && (
                        <p className="text-xs text-slate-500 italic mt-1">
                          "{entry.comment}"
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══ COMMENTS TAB ═══ */}
        {activeTab === "comments" && activity && (
          <CommentsPanel
            entityType="activity"
            entityId={activity.id}
            onCountChange={onCommentCountChange}
            composerActionsClassName="pr-20"
          />
        )}
      </div>

      {/* Undo Confirmation Dialog for Actualized Activities */}
      <AlertDialog
        open={showUndoConfirmDialog}
        onOpenChange={setShowUndoConfirmDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Undo Actualization?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>This activity has been actualized. Undoing will:</p>
              <ul className="list-disc list-inside text-sm space-y-1 text-slate-600">
                <li>
                  Remove the{" "}
                  {undoStack[0]?.field === "actual_finish"
                    ? "Actual Finish"
                    : "Actual Start"}{" "}
                  date
                </li>
                <li>Restore previous dates and status</li>
                <li>Trigger schedule recalculation</li>
              </ul>
              <p className="font-medium text-slate-700 pt-2">
                Are you sure you want to undo this change?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUndo}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Yes, Undo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ─── Delete activity confirmation ───
          onOpenChange is guarded against close attempts while the DELETE
          request is in flight (isDeleting=true). This prevents the user
          from dismissing the dialog via backdrop click or Escape mid-call
          and then seeing a "deleted" toast appear without a corresponding
          dialog confirmation — the dialog stays until the request settles. */}
      <AlertDialog
        open={showDeleteConfirm}
        onOpenChange={(open) => {
          if (isDeleting) return;
          setShowDeleteConfirm(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete activity?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <strong>{activity?.name}</strong> and
              all its predecessor/successor relationships. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteActivity}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {isDeleting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting…</>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ActivityBottomPanel;
